import type { StatusTone } from '@/components/px/StatusPill'
import {
  GROUP_STATS,
  MATCH_CANDIDATES,
  MATCH_POOL_SIZE,
  NOW,
  OPEN_TASK,
  heroProject,
  personById,
  type MatchCandidate,
} from '@/demo/data'

/**
 * Screen 1.4 — Allocation. All copy, ranking rows and destinations for the
 * open-task surface, composed from the frozen seed in `@/demo/data`: the
 * page component holds no literal figure, name or route.
 *
 * The written trade-offs (IMPACT), the growth-move prose and the override
 * reason are the deck's own words and are carried verbatim.
 */

/** A run of prose in which some words are real destinations. */
export type Segment =
  | { kind: 'text'; text: string }
  | { kind: 'link'; label: string; href: string }

export const POOL_HREF = '/resources/pool'
export const CAPACITY_HREF = '/resources/capacity'

/** Profile route for a seeded person id. */
export const personHref = (personId: string): string => `/resources/people/${personId}`

const hero = heroProject()
const arta = personById('arta-berisha')
const julien = personById('julien-roux')
const approver = personById('niels-jansen')
const firstName = (name: string): string => name.split(' ')[0] ?? name

/** Hours Julien reviews alongside Arta — the price of the growth move. */
export const PAIRING_HOURS = 8

/* ── Chrome ─────────────────────────────────────────────────────────────── */

export const ALLOCATION_CHROME = {
  moduleLabel: 'PX · Resources',
  breadcrumb: 'Allocations › Open task',
  contextPill: `${GROUP_STATS.resources.openTasksToAllocate} OPEN REQUESTS`,
  /** Screen title for assistive tech — the banner renders the visible one. */
  documentTitle: 'Allocation · open task',
} as const

/* ── 1. The open task (aubergine banner) ────────────────────────────────── */

export const OPEN_TASK_BANNER = {
  eyebrow: `OPEN TASK · FROM ${OPEN_TASK.from}`,
  title: OPEN_TASK.title,
  meta: `${OPEN_TASK.hours} h · ${OPEN_TASK.skills} · starts in ${OPEN_TASK.startsInDays} days · ${OPEN_TASK.phase}`,
} as const

export const OPEN_TASK_PROJECT = {
  label: hero.client,
  href: `/project/${hero.id}`,
} as const

/* ── 2. The PX match (mint panel) ───────────────────────────────────────── */

export const MATCH_SECTION_LABEL = 'PX MATCH — SKILL × AVAILABILITY × RATE'

export const MATCH_COLUMNS = ['CANDIDATE', 'SKILL FIT', 'FREE', 'RATE', 'IMPACT'] as const

/** The tag column carries a pill, not a heading — named for screen readers. */
export const MATCH_TAG_COLUMN_LABEL = 'PX tag'

export const MATCH_TABLE_CAPTION =
  'Candidates ranked by skill fit, availability and rate, with the trade-off each choice carries.'

/** Locked tag → pill tone mapping (StatusPill's canonical AA set). */
const TAG_TONE: Record<MatchCandidate['tag'], StatusTone> = {
  'Best fit': 'done',
  Cheaper: 'neutral',
  'Growth move': 'people',
  Fallback: 'neutral',
}

export interface CandidateRow {
  key: string
  initial: string
  name: string
  detail: string
  /** null for the fallback row — a new hire has no profile to open. */
  href: string | null
  fitPct: number | null
  freePct: number | null
  rateLabel: string
  impact: string
  tag: MatchCandidate['tag']
  tone: StatusTone
}

export const CANDIDATE_ROWS: readonly CandidateRow[] = MATCH_CANDIDATES.map((candidate) => {
  const person = candidate.personId === null ? null : personById(candidate.personId)
  return {
    key: candidate.personId ?? candidate.label,
    initial: person === null ? '+' : person.initial,
    name: candidate.label,
    detail: person === null ? candidate.detail : `${person.office} · ${candidate.detail}`,
    href: candidate.personId === null ? null : personHref(candidate.personId),
    fitPct: candidate.fitPct,
    freePct: candidate.freePct,
    rateLabel: candidate.rateLabel,
    impact: candidate.impact,
    tag: candidate.tag,
    tone: TAG_TONE[candidate.tag],
  }
})

/** Under the table: what the ranking withholds, and where the rest lives. */
export const MATCH_DENSITY_LINE: readonly Segment[] = [
  { kind: 'text', text: `Showing the top ${CANDIDATE_ROWS.length} of ${MATCH_POOL_SIZE} ranked · ` },
  { kind: 'link', label: 'view all', href: POOL_HREF },
]

/* ── 3. The growth move (mint panel) ────────────────────────────────────── */

export const GROWTH_PANEL = {
  label: 'IF YOU PICK THE GROWTH MOVE',
  prose: [
    {
      kind: 'text',
      text: `${firstName(arta.name)} delivers with ${PAIRING_HOURS} h of ${firstName(julien.name)}'s review. Margin on the task rises from 41% to 58% and Kosovo gains a second certified migration engineer — the constraint flagged in `,
    },
    { kind: 'link', label: 'the W41 forecast', href: CAPACITY_HREF },
    { kind: 'text', text: '.' },
  ] as readonly Segment[],
} as const

/* ── 4. The decision (already taken in the deck) ────────────────────────── */

export const DECIDED_AT = '10:47'

export const DECIDED_ON_LABEL = new Intl.DateTimeFormat('en-GB', {
  timeZone: 'Europe/Amsterdam',
  day: 'numeric',
  month: 'short',
  year: 'numeric',
}).format(NOW)

export const DECISION = {
  summary: [
    { kind: 'text', text: 'Booked to ' },
    { kind: 'link', label: arta.name, href: personHref(arta.id) },
    { kind: 'text', text: ` · ${OPEN_TASK.hours} h · pairing ${PAIRING_HOURS} h with ` },
    { kind: 'link', label: julien.name, href: personHref(julien.id) },
  ] as readonly Segment[],
  approvedBy: firstName(approver.name),
  approvedAt: DECIDED_AT,
  consequences: ['Client informed', 'Timesheet opened'] as readonly string[],
  loggedLine: 'PX books the hours only after sign-off. The override and the reason are logged.',
  overrideHref: '#override-record',
} as const

/** Re-opening a booking is a decision too — it states what it costs first. */
export const CHANGE_BOOKING = {
  trigger: 'Change the booking',
  heading: 'Re-open this booking?',
  consequence: `Re-opening this booking un-books ${OPEN_TASK.hours} h, notifies the client, and records the change under your name.`,
  confirm: 'Yes, re-open the booking',
  cancel: 'Keep the booking',
  confirmed:
    'Recorded. Nothing is un-booked in this round — the consequence above is what a real confirmation would trigger.',
} as const

/* ── 5. The override record (the target of `view override reason →`) ───── */

export interface OverrideRow {
  term: string
  value: readonly Segment[]
}

export const OVERRIDE_RECORD = {
  id: 'override-record',
  label: 'OVERRIDE RECORD',
  rows: [
    {
      term: 'Decision',
      value: [
        { kind: 'text', text: 'Growth move booked over the best fit — ' },
        { kind: 'link', label: arta.name, href: personHref(arta.id) },
        { kind: 'text', text: ' instead of ' },
        { kind: 'link', label: julien.name, href: personHref(julien.id) },
        { kind: 'text', text: '.' },
      ],
    },
    {
      term: 'Task',
      value: [
        { kind: 'text', text: `${OPEN_TASK.title} · ${OPEN_TASK.hours} h · ` },
        { kind: 'link', label: OPEN_TASK_PROJECT.label, href: OPEN_TASK_PROJECT.href },
      ],
    },
    {
      term: 'Who',
      value: [{ kind: 'text', text: `${approver.name} · ${approver.role} · ${approver.office}` }],
    },
    { term: 'When', value: [{ kind: 'text', text: `${DECIDED_ON_LABEL} · ${DECIDED_AT}` }] },
    {
      term: 'Reason',
      value: [
        {
          kind: 'text',
          text: 'Growth move chosen over best fit — margin + Kosovo certification, per W41 forecast',
        },
      ],
    },
  ] as readonly OverrideRow[],
  footnote: 'Every override teaches the matcher what the group values — the reason is part of the record.',
} as const

/* ── 6. What the surface withholds ──────────────────────────────────────── */

export const ALLOCATION_DENSITY_NOTE = `Showing the top ${CANDIDATE_ROWS.length} of ${MATCH_POOL_SIZE} ranked — the rest of the pool stands one click away, and every candidate name opens that person's profile. PX ranks; the hours are booked only when a named human signs off.`
